import { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { getNotificationList } from "store/reducers/common.slice";
import {
  getNotificationType,
  getTodayPopupSeenStorageKey,
} from "utils/notificationHelpers";

export default function MobileNotificationPopupQueue() {
  const dispatch = useDispatch();
  const inProgress = useRef(false);
  const { userRole } = useSelector(({ auth }) => auth);
  const { notificationList = [] } = useSelector(({ common }) => common);

  const popupNotifications = useMemo(
    () =>
      (Array.isArray(notificationList) ? notificationList : []).filter(
        (item) => getNotificationType(item) === 2
      ),
    [notificationList]
  );

  useEffect(() => {
    dispatch(getNotificationList());
  }, [dispatch]);

  useEffect(() => {
    if (userRole === "Super Admin") return;
    if (inProgress.current || popupNotifications.length === 0) return;

    const storageKey = getTodayPopupSeenStorageKey();
    const getNotificationKey = (item) =>
      String(
        item?.id ||
          item?.notificationId ||
          `${item?.notificationDate || ""}|${
            item?.notificationTitle || ""
          }|${item?.notificationText || ""}`
      );

    let seenNotificationIds = [];
    try {
      const storedIds = JSON.parse(
        sessionStorage.getItem(storageKey) || "[]"
      );
      seenNotificationIds = Array.isArray(storedIds) ? storedIds : [];
    } catch (error) {
      seenNotificationIds = [];
    }

    const seenSet = new Set(seenNotificationIds);
    const unseenNotifications = popupNotifications.filter(
      (item) => !seenSet.has(getNotificationKey(item))
    );
    if (unseenNotifications.length === 0) return;

    const escapeHtml = (value) =>
      String(value || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");

    const getDisplayDateTime = (item) => {
      const rawDate =
        item?.notificationDate ||
        item?.updatedAt ||
        item?.createdAt ||
        item?.date ||
        item?.createdDate;
      const date = rawDate ? new Date(rawDate) : new Date();
      if (Number.isNaN(date.getTime())) return "";
      return date.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    };

    inProgress.current = true;

    const showNotificationsSequentially = async () => {
      for (const item of unseenNotifications) {
        const popupTitle =
          item?.notificationTitle || "New Notification";
        const popupText = escapeHtml(item?.notificationText || "");
        const popupTime = escapeHtml(getDisplayDateTime(item));

        await Swal.fire({
          title: "",
          html: `
            <div class="penguin-notify-card">
              <div class="penguin-notify-head">
                <span class="penguin-notify-icon material-symbols-outlined">notifications</span>
                <div class="penguin-notify-title">${escapeHtml(
                  popupTitle
                )}</div>
              </div>
              <div class="penguin-notify-body">
                <div class="penguin-notify-text">${popupText}</div>
                <div class="penguin-notify-time">${popupTime}</div>
              </div>
              <div class="penguin-notify-foot">
                <button id="mobile-notification-popup-ok-btn" type="button" class="penguin-notify-ok">
                  OK
                </button>
              </div>
            </div>
          `,
          width: 360,
          background: "transparent",
          padding: "0",
          showCloseButton: false,
          showConfirmButton: false,
          allowEscapeKey: false,
          allowOutsideClick: false,
          customClass: {
            popup: "penguin-notify-swal",
            container: "notification-today-popup",
          },
          didOpen: () => {
            const container = Swal.getContainer();
            if (container) container.style.zIndex = "20000";
            const button = document.getElementById(
              "mobile-notification-popup-ok-btn"
            );
            if (button) {
              button.addEventListener("click", () => Swal.close(), {
                once: true,
              });
            }
          },
        });
        seenSet.add(getNotificationKey(item));
        sessionStorage.setItem(storageKey, JSON.stringify([...seenSet]));
      }
      inProgress.current = false;
    };

    showNotificationsSequentially().catch(() => {
      inProgress.current = false;
    });
  }, [popupNotifications, userRole]);

  return null;
}
